import type { ChatLogMessageItem } from '../services/api';

const MSG_CHITCHAT_REPLY = 'Hey, how can I help you with your SOPs and documents?';
const MSG_ANSWER_NOT_IN_DOCUMENTS =
    'The answer is not available in the provided documents. Please contact your administrator.';
const MSG_LIVE_INFO_CHITCHAT_REPLY =
    "I can't look up live weather, news, or the time—I can help with your SOPs and documents. What do you need?";
const MSG_QUERY_NOT_RELATED =
    'I cannot answer this question from the processed documents. Missing: relevant content in the uploaded files that matches your query.';

/** Assistant replies that must never show a document/SOP source line. */
const HIDE_SOURCE_EXACT_MESSAGES = new Set([
    MSG_CHITCHAT_REPLY,
    MSG_ANSWER_NOT_IN_DOCUMENTS,
    MSG_LIVE_INFO_CHITCHAT_REPLY,
    MSG_QUERY_NOT_RELATED,
]);

/** Prefixes aligned with server non-document / error assistant responses. */
const HIDE_SOURCE_PREFIXES = [
    'I cannot answer this question from the processed documents',
    'I cannot answer this question because it appears unrelated',
    'You do not have access to this document.',
    'No processed documents available',
    'No documents are accessible to your user role',
    'No document retriever available',
    'You have document access, but no processed retriever',
    'Error',
    'I found relevant information but encountered an error',
] as const;

/** Format session-level SOP reference string for table/summary display. */
export function formatSopReferences(sopReferenced: string | undefined | null): string {
    if (!sopReferenced || sopReferenced.trim() === '' || sopReferenced === '-') {
        return '-';
    }

    if (sopReferenced.includes(':')) {
        return sopReferenced;
    }

    if (sopReferenced.match(/\bSOP-[A-Z]{2,}-\d{2,4}\b/i)) {
        const codes = sopReferenced.split(',').map((code) => code.trim());
        return codes.map((code) => `${code}`).join(', ');
    }

    return sopReferenced;
}

export function shouldHideAssistantSourceLine(text: string): boolean {
    const t = (text || '').trim();
    if (!t) return true;
    if (HIDE_SOURCE_EXACT_MESSAGES.has(t)) return true;
    if (HIDE_SOURCE_PREFIXES.some((prefix) => t.startsWith(prefix))) return true;
    return false;
}

/** Strip document images for non-document assistant replies (same rules as source line). */
export function getAssistantImagesForDisplay(
    answer: string,
    images: string[] | undefined | null,
): string[] {
    if (shouldHideAssistantSourceLine(answer)) return [];
    return images || [];
}

/**
 * Per-assistant-message source line only (never session-level sopReferenced).
 */
export function resolveAssistantSourceLine(m: ChatLogMessageItem): string | null {
    if (m.role !== 'assistant') return null;
    if (shouldHideAssistantSourceLine(m.text || '')) return null;

    if (m.sopSource?.sopName) {
        const tail = m.sopSource.sectionTitle ? `, ${m.sopSource.sectionTitle}` : '';
        return `📄 Sourced from SOP: ${m.sopSource.sopName}${tail}`;
    }

    const rawSf = m.sourcedFrom;
    if (rawSf != null) {
        if (Array.isArray(rawSf)) {
            const parts = rawSf
                .map((entry) => {
                    if (typeof entry === 'string') return entry.trim();
                    if (entry && typeof entry === 'object') {
                        const o = entry as { documentName?: string; sectionName?: string };
                        return [o.documentName, o.sectionName].filter(Boolean).join(' — ');
                    }
                    return '';
                })
                .filter(Boolean);
            if (parts.length) return `📄 Sourced from: ${parts.join(', ')}`;
        } else if (typeof rawSf === 'object') {
            const o = rawSf as { documentName?: string; sectionName?: string };
            if (o.documentName && o.documentName !== 'Unknown Document') {
                const sec = o.sectionName ? ` — ${o.sectionName}` : '';
                return `📄 Sourced from: ${o.documentName}${sec}`;
            }
        }
    }

    return null;
}
